import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const MarketplaceManager = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [channels, setChannels] = useState([]);
  const [products, setProducts] = useState([
    { id: 1, name: 'Premium Widget Pro', sku: 'WGT-001', price: 99.99, stock: 45, marketplaces: ['Amazon', 'eBay'], status: 'active' },
    { id: 2, name: 'Basic Widget', sku: 'WGT-002', price: 49.99, stock: 120, marketplaces: ['Amazon'], status: 'active' },
    { id: 3, name: 'Widget Bundle Pack', sku: 'WGT-BUNDLE', price: 199.99, stock: 8, marketplaces: ['Amazon', 'eBay', 'Shopify'], status: 'active' }
  ]);
  const [listings, setListings] = useState([
    { id: 1, product: 'Premium Widget Pro', marketplace: 'Amazon', status: 'active', price: 99.99, lastSync: '2025-11-26T10:00:00', sales: 23 },
    { id: 2, product: 'Basic Widget', marketplace: 'Amazon', status: 'active', price: 49.99, lastSync: '2025-11-26T09:30:00', sales: 67 },
    { id: 3, product: 'Widget Bundle Pack', marketplace: 'eBay', status: 'pending', price: 199.99, lastSync: '2025-11-25T18:00:00', sales: 5 }
  ]);
  const [orders, setOrders] = useState([
    { id: 1001, marketplace: 'Amazon', customer: 'John Doe', product: 'Premium Widget Pro', amount: 99.99, status: 'shipped', date: '2025-11-26T08:30:00' },
    { id: 1002, marketplace: 'eBay', customer: 'Jane Smith', product: 'Basic Widget', amount: 49.99, status: 'processing', date: '2025-11-26T09:15:00' },
    { id: 1003, marketplace: 'Shopify', customer: 'Mike Johnson', product: 'Widget Bundle Pack', amount: 199.99, status: 'pending', date: '2025-11-26T10:00:00' }
  ]);
  const [stats, setStats] = useState({
    totalProducts: 48,
    activeListings: 142,
    totalOrders: 256,
    totalRevenue: 12485.50
  });

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      const response = await api.get('/marketplace/channels');
      if (response.data.channels) {
        setChannels(response.data.channels);
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  const getMarketplaceIcon = (marketplace) => {
    const icons = {
      'Amazon': '📦',
      'eBay': '🏪',
      'Shopify': '🛍️',
      'Etsy': '🎨',
      'WooCommerce': '🛒'
    };
    return icons[marketplace] || '🏬';
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Marketplace Manager</span>
      </nav>

      {/* Main Card */}
      <div className="card" style={{ background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🛒 Marketplace Manager
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage marketplace listings, products, and e-commerce integrations
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: `2px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
            gap: 'var(--space-1)'
          }}>
            {[
              { key: 'overview', label: '📊 Overview' },
              { key: 'products', label: '📦 Products' },
              { key: 'listings', label: '🏷️ Listings' },
              { key: 'orders', label: '🛍️ Orders' },
              { key: 'channels', label: '🔌 Channels' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'none',
                  border: 'none',
                  borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: activeTab === tab.key ? 'var(--primary-600)' : isDark ? '#9ca3af' : 'var(--gray-500)',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                { label: 'Total Products', value: stats.totalProducts, icon: '📦', color: '#3b82f6' },
                { label: 'Active Listings', value: stats.activeListings, icon: '🏷️', color: '#10b981' },
                { label: 'Total Orders', value: stats.totalOrders, icon: '🛍️', color: '#f59e0b' },
                { label: 'Total Revenue', value: `$${stats.totalRevenue.toLocaleString()}`, icon: '💰', color: '#8b5cf6' }
              ].map((stat, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '1.5rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    borderLeft: `4px solid ${stat.color}`,
                    backgroundColor: isDark ? '#374151' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{stat.icon}</span>
                    <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                      {stat.label}
                    </div>
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stat.color }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Connected Channels */}
            <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Connected Marketplaces</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {['Amazon', 'eBay', 'Shopify'].map((marketplace, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '1.25rem',
                    border: '1px solid var(--primary-600)',
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#1e3a5f' : '#eff6ff'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '2rem' }}>{getMarketplaceIcon(marketplace)}</span>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '1.05rem', color: isDark ? '#f9fafb' : 'inherit' }}>{marketplace}</div>
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#10b981'
                      }}>
                        ● Connected
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                    {listings.filter(l => l.marketplace === marketplace).length} active listings
                  </div>
                </div>
              ))}
              <div
                style={{
                  padding: '1.25rem',
                  border: `2px dashed ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#374151' : 'var(--gray-50)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/marketplace/channels/new')}
              >
                <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>➕</span>
                <div style={{ fontWeight: '600', color: 'var(--primary-600)' }}>
                  Add Marketplace
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Recent Orders</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {orders.slice(0, 3).map(order => (
                <div
                  key={order.id}
                  style={{
                    padding: '1.25rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#374151' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                        Order #{order.id}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        {getMarketplaceIcon(order.marketplace)} {order.marketplace} • {order.customer} • {order.product}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: '600', color: 'var(--primary-600)', marginBottom: '0.25rem' }}>
                        ${order.amount.toFixed(2)}
                      </div>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: order.status === 'shipped' ? '#10b981' : order.status === 'processing' ? '#f59e0b' : '#6b7280',
                        color: 'white',
                        textTransform: 'capitalize'
                      }}>
                        {order.status}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : 'inherit' }}>Product Catalog</h3>
              <button className="btn btn-primary">
                ➕ Add Product
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {products.map(product => (
                <div
                  key={product.id}
                  style={{
                    padding: '1.25rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#374151' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1.05rem', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.5rem' }}>
                        SKU: {product.sku}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {product.marketplaces.map((mp, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: isDark ? '#4b5563' : 'var(--gray-100)',
                              color: isDark ? '#e5e7eb' : 'var(--gray-700)'
                            }}
                          >
                            {getMarketplaceIcon(mp)} {mp}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-600)', marginBottom: '0.25rem' }}>
                        ${product.price.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        Stock: <strong style={{ color: isDark ? '#f9fafb' : 'inherit' }}>{product.stock}</strong>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      ✏️ Edit
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      🔄 Sync to Marketplaces
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : 'inherit' }}>Marketplace Listings</h3>
              <button className="btn btn-primary">
                🔄 Sync All
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {listings.map(listing => (
                <div
                  key={listing.id}
                  style={{
                    padding: '1.25rem',
                    border: `1px solid ${listing.status === 'active' ? 'var(--primary-600)' : isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: listing.status === 'active' ? (isDark ? '#1e3a5f' : '#eff6ff') : (isDark ? '#374151' : 'white')
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{getMarketplaceIcon(listing.marketplace)}</span>
                        <div>
                          <div style={{ fontWeight: '600', color: isDark ? '#f9fafb' : 'inherit' }}>{listing.product}</div>
                          <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                            {listing.marketplace}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        Last synced: {new Date(listing.lastSync).toLocaleString()} • {listing.sales} sales
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-600)', marginBottom: '0.25rem' }}>
                        ${listing.price.toFixed(2)}
                      </div>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: listing.status === 'active' ? '#10b981' : '#f59e0b',
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {listing.status}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      👁️ View on {listing.marketplace}
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      🔄 Sync Now
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      ⏸️ Pause
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>Marketplace Orders</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {orders.map(order => (
                <div
                  key={order.id}
                  style={{
                    padding: '1.25rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#374151' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1.05rem', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                        Order #{order.id}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.5rem' }}>
                        {getMarketplaceIcon(order.marketplace)} {order.marketplace} • {new Date(order.date).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'inherit' }}>
                        <strong>Customer:</strong> {order.customer}<br />
                        <strong>Product:</strong> {order.product}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-600)', marginBottom: '0.5rem' }}>
                        ${order.amount.toFixed(2)}
                      </div>
                      <div style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        backgroundColor: order.status === 'shipped' ? '#10b981' : order.status === 'processing' ? '#f59e0b' : '#6b7280',
                        color: 'white',
                        textTransform: 'capitalize'
                      }}>
                        {order.status}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      👁️ View Details
                    </button>
                    {order.status === 'pending' && (
                      <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}>
                        ✓ Process Order
                      </button>
                    )}
                    {order.status === 'processing' && (
                      <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}>
                        🚚 Mark as Shipped
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Channels Tab */}
        {activeTab === 'channels' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : 'inherit' }}>Marketplace Channels</h3>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/marketplace/channels/new')}
              >
                ➕ Connect New Channel
              </button>
            </div>

            <div style={{
              padding: '1.5rem',
              border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
              borderRadius: '8px',
              backgroundColor: isDark ? '#374151' : 'var(--gray-50)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔌</div>
              <h4 style={{ marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>Manage Channel Connections</h4>
              <p style={{ color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '1rem' }}>
                Connect and configure your marketplace channels to start selling across multiple platforms.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/marketplace/channels')}
              >
                Go to Channels Management
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceManager;
